import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { withFlowType } from 'helpers/assistantFlow';
import type {
  HiringAssistantContinuePayload,
  HiringAssistantContinueResponse,
} from './types';

/**
 * Generic hook for the hiring assistant API
 */
export const useHiringAssistant = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (payload: HiringAssistantContinuePayload) => {
      const response = await apiClient.post('/assessment/hiring-assistant/', withFlowType(payload));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hiringAssistant', assessmentId] });
      queryClient.invalidateQueries({ queryKey: ['conversationSessions'] });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to send request';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    response: mutation.data,
    loading: mutation.isPending,
    error: mutation.error,
    sendRequest: mutation.mutate,
  };
};

/**
 * Hook for continuing the hiring assistant conversation
 */
export const useHiringAssistantContinue = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (payload: HiringAssistantContinuePayload) => {
      const response = await apiClient.post<HiringAssistantContinueResponse>('/assessment/hiring-assistant/', withFlowType(payload));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hiringAssistant', assessmentId] });
      queryClient.invalidateQueries({ queryKey: ['conversationSessions'] });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to send request';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    response: mutation.data,
    isLoading: mutation.isPending,
    error: mutation.error,
    sendRequest: mutation.mutate,
  };
};
